export default function directoryMap () {
    const directory = document.querySelector('.directory');
    const nav = document.querySelector('.directory-nav');

    if (!directory || !nav || typeof google === 'undefined' || !google.maps) {
        return;
    }

    const elements = {
        map: directory.querySelector('.directory__map'),
        leftColumn: directory.querySelector('.directory__left'),
        list: directory.querySelector('.directory__list'),
        searchInput: directory.querySelector('.directory__search-input'),
        searchButton: directory.querySelector('.directory__search-button'),
        resultsCount: directory.querySelector('.directory__results-count'),
        resultsSuffix: directory.querySelector('.directory__results-text--suffix'),
        resultsMainCategory: directory.querySelector('.directory__results-category--main'),
        resultsSeparator: directory.querySelector('.directory__results-separator'),
        resultsSubCategory: directory.querySelector('.directory__results-category--sub')
    };

    if (!elements.map || !elements.list || !elements.leftColumn) {
        return;
    }

    const cards = Array.from(elements.list.querySelectorAll('.directory-card'));

    const navItems = Array.from(nav.querySelectorAll('.directory-nav__item'));
    const mainButtons = Array.from(nav.querySelectorAll('.directory-nav__button'));
    const subButtons = Array.from(nav.querySelectorAll('.directory-nav__dropdown-button'));

    const state = {
        mainCategory: 'all',
        subCategory: '',
        searchQuery: '',
        openCategory: ''
    };

    const markerState = {
        map: null,
        markers: [],
        markerByCard: new Map(),
        cardByMarker: new Map(),
        activeMarker: null,
        activeCard: null,
        isInitialRender: true
    };

    const iconSize = new google.maps.Size(32, 42);

    const icons = {
        default: {
            url: '/wp-content/themes/weehawke-2242/assets/src/img/pin-default.svg',
            scaledSize: iconSize
        },
        active: {
            url: '/wp-content/themes/weehawke-2242/assets/src/img/pin-active.svg',
            scaledSize: iconSize
        }
    };

    function initMap () {
        markerState.map = new google.maps.Map(elements.map, {
            center: { lat: 40.76, lng: -74.02 },
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
        });
    }

    function getCardData (card) {
        return {
            title: card.querySelector('.directory-card__title')?.textContent.trim().toLowerCase() || '',
            mainCategory: card.dataset.mainCategory || '',
            subCategory: card.dataset.subCategory || '',
            lat: parseFloat(card.dataset.lat),
            lng: parseFloat(card.dataset.lng)
        };
    }

    function getVisibleCards () {
        const query = state.searchQuery.trim().toLowerCase();

        return cards.filter((card) => {
            const data = getCardData(card);

            const matchesMainCategory =
                state.mainCategory === 'all' || data.mainCategory === state.mainCategory;

            const matchesSubCategory =
                !state.subCategory || data.subCategory === state.subCategory;

            const matchesSearch =
                !query || data.title.includes(query);

            return matchesMainCategory && matchesSubCategory && matchesSearch;
        });
    }

    function updateCardVisibility (visibleCards) {
        const visibleSet = new Set(visibleCards);

        cards.forEach((card) => {
            card.style.display = visibleSet.has(card) ? '' : 'none';
        });
    }

    function resetActiveMarker () {
        if (!markerState.activeMarker) {
            return;
        }

        markerState.activeMarker.setIcon(icons.default);
        markerState.activeMarker = null;
    }

    function resetActiveCard () {
        if (!markerState.activeCard) {
            return;
        }

        markerState.activeCard.classList.remove('directory-card--active');
        markerState.activeCard = null;
    }

    function highlightCard (card) {
        if (!card || markerState.activeCard === card) {
            return;
        }

        resetActiveCard();
        card.classList.add('directory-card--active');
        markerState.activeCard = card;
    }

    function scrollToCard (card) {
        if (!card) {
            return;
        }

        const leftRect = elements.leftColumn.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const currentScroll = elements.leftColumn.scrollTop;
        const offset = 24;
        const targetScroll = currentScroll + (cardRect.top - leftRect.top) - offset;

        elements.leftColumn.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
        });
    }

    function setActiveMarker (marker, shouldFocus = true) {
        if (!marker) {
            return;
        }

        if (markerState.activeMarker && markerState.activeMarker !== marker) {
            markerState.activeMarker.setIcon(icons.default);
        }

        marker.setIcon(icons.active);
        markerState.activeMarker = marker;

        if (shouldFocus) {
            markerState.map.panTo(marker.getPosition());
            markerState.map.setZoom(15);
        }
    }

    function activateMarkerByCard (card, shouldFocusMap = true, shouldScrollCard = false) {
        if (!card) {
            return;
        }

        const marker = markerState.markerByCard.get(card);

        if (!marker) {
            return;
        }

        setActiveMarker(marker, shouldFocusMap);

        if (shouldScrollCard) {
            scrollToCard(card);
        }

        highlightCard(card);
    }

    function activateCardByMarker (marker, shouldFocusMap = true) {
        if (!marker) {
            return;
        }

        const card = markerState.cardByMarker.get(marker);

        setActiveMarker(marker, shouldFocusMap);

        if (!card) {
            return;
        }

        scrollToCard(card);
        highlightCard(card);
    }

    function clearMarkers () {
        markerState.markers.forEach((marker) => marker.setMap(null));
        markerState.markers = [];
        markerState.markerByCard.clear();
        markerState.cardByMarker.clear();
        markerState.activeMarker = null;
    }

    function updateMarkers (visibleCards) {
        clearMarkers();

        if (!visibleCards.length) {
            return;
        }

        const bounds = new google.maps.LatLngBounds();

        visibleCards.forEach((card) => {
            const data = getCardData(card);

            if (Number.isNaN(data.lat) || Number.isNaN(data.lng)) {
                return;
            }

            const marker = new google.maps.Marker({
                position: { lat: data.lat, lng: data.lng },
                map: markerState.map,
                title: data.title,
                icon: icons.default
            });

            marker.addListener('click', () => {
                activateCardByMarker(marker, true);
            });

            markerState.markers.push(marker);
            markerState.markerByCard.set(card, marker);
            markerState.cardByMarker.set(marker, card);

            bounds.extend(marker.getPosition());
        });

        if (visibleCards.length === 1) {
            activateSingleVisibleCard(visibleCards[0]);
            return;
        }

        markerState.map.fitBounds(bounds);

        if (!markerState.isInitialRender) {
            activateMarkerByCard(visibleCards[0], false, false);
        }
    }

    function activateSingleVisibleCard (card) {
        const marker = markerState.markerByCard.get(card);

        if (!marker) {
            return;
        }

        markerState.map.setCenter(marker.getPosition());
        markerState.map.setZoom(15);
        activateMarkerByCard(card, false, false);
    }

    function updateResults (visibleCards) {
        const {
            resultsCount,
            resultsSuffix,
            resultsMainCategory,
            resultsSeparator,
            resultsSubCategory
        } = elements;

        if (!resultsCount || !resultsSuffix || !resultsMainCategory || !resultsSeparator || !resultsSubCategory) {
            return;
        }

        const activeMainButton = nav.querySelector('.directory-nav__button--active');
        const activeSubButton = nav.querySelector('.directory-nav__dropdown-button--active');

        resultsCount.textContent = String(visibleCards.length);

        const isAllCategory = state.mainCategory === 'all';

        resultsSuffix.style.display = isAllCategory ? 'none' : '';
        resultsMainCategory.style.display = isAllCategory ? 'none' : '';
        resultsSeparator.style.display = 'none';
        resultsSubCategory.style.display = 'none';

        if (isAllCategory) {
            resultsMainCategory.textContent = '';
            resultsSubCategory.textContent = '';
            return;
        }

        resultsMainCategory.textContent = activeMainButton?.textContent.trim() || '';

        if (activeSubButton) {
            resultsSeparator.style.display = '';
            resultsSubCategory.style.display = '';
            resultsSubCategory.textContent = activeSubButton.textContent.trim();
        } else {
            resultsSubCategory.textContent = '';
        }
    }

    function updateNavigationState () {
        navItems.forEach((item) => {
            const mainButton = item.querySelector('.directory-nav__button');
            const dropdown = item.querySelector('.directory-nav__dropdown');
            const mainCategory = mainButton?.dataset.mainCategory || '';
            const isActive = mainCategory === state.mainCategory;
            const isOpen = Boolean(dropdown) && mainCategory === state.openCategory;

            item.classList.toggle('directory-nav__item--active', isActive);
            item.classList.toggle('directory-nav__item--open', isOpen);
        });

        mainButtons.forEach((button) => {
            button.classList.toggle(
                'directory-nav__button--active',
                button.dataset.mainCategory === state.mainCategory
            );
        });

        subButtons.forEach((button) => {
            button.classList.toggle(
                'directory-nav__dropdown-button--active',
                button.dataset.mainCategory === state.mainCategory &&
                button.dataset.subCategory === state.subCategory
            );
        });
    }

    function closeNavigationDropdown () {
        if (!state.openCategory) {
            return;
        }

        state.openCategory = '';
        updateNavigationState();
    }

    function applyFilters () {
        const visibleCards = getVisibleCards();

        resetActiveCard();
        updateNavigationState();
        updateCardVisibility(visibleCards);
        updateResults(visibleCards);
        updateMarkers(visibleCards);

        if (markerState.isInitialRender) {
            markerState.isInitialRender = false;
        }
    }

    function goToLink (button) {
        const link = button?.dataset?.link;

        if (!link) {
            return false;
        }

        window.location.href = link;
        return true;
    }

    function bindNavigationEvents () {
        nav.addEventListener('click', (event) => {
            const subButton = event.target.closest('.directory-nav__dropdown-button');

            if (subButton) {
                event.preventDefault();

                if (goToLink(subButton)) {
                    return;
                }

                state.mainCategory = subButton.dataset.mainCategory || 'all';
                state.subCategory = subButton.dataset.subCategory || '';
                state.openCategory = '';

                applyFilters();
                return;
            }

            const mainButton = event.target.closest('.directory-nav__button');

            if (!mainButton) {
                return;
            }

            event.preventDefault();

            if (goToLink(mainButton)) {
                return;
            }

            const parentItem = mainButton.closest('.directory-nav__item');
            const dropdown = parentItem ? parentItem.querySelector('.directory-nav__dropdown') : null;
            const nextMainCategory = mainButton.dataset.mainCategory || 'all';

            state.mainCategory = nextMainCategory;
            state.subCategory = '';

            if (nextMainCategory === 'all') {
                state.openCategory = '';
                applyFilters();
                return;
            }

            if (dropdown) {
                state.openCategory = state.openCategory === nextMainCategory ? '' : nextMainCategory;
                applyFilters();
                return;
            }

            state.openCategory = '';
            applyFilters();
        });
    }

    function bindCardEvents () {
        elements.list.addEventListener('click', (event) => {
            const card = event.target.closest('.directory-card');

            if (!card) {
                return;
            }

            activateMarkerByCard(card, true, false);
        });
    }

    function bindSearchEvents () {
        if (!elements.searchInput) {
            return;
        }

        let timeoutId = null;

        const handleSearch = () => {
            state.searchQuery = elements.searchInput.value || '';
            applyFilters();
        };

        elements.searchInput.addEventListener('input', () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleSearch, 250);
        });

        elements.searchButton?.addEventListener('click', handleSearch);
    }

    function bindMapEvents () {
        markerState.map.addListener('click', () => {
            resetActiveMarker();
            resetActiveCard();
        });
    }

    function setInitialStateFromUrl () {
        const params = new URLSearchParams(window.location.search);
        const category = params.get('cat');

        if (!category) {
            return;
        }

        const categoryButton = mainButtons.find((button) => {
            return button.dataset.mainCategory === category;
        });

        if (!categoryButton) {
            return;
        }

        state.mainCategory = category;
        state.subCategory = '';
        state.openCategory = '';
    }

    function setInitialStateFromPageCard () {
        const firstCard = cards[0];

        if (!firstCard) {
            return;
        }

        const data = getCardData(firstCard);

        if (!data.mainCategory) {
            return;
        }

        state.mainCategory = data.mainCategory;
        state.subCategory = data.subCategory || '';
        state.openCategory = '';
    }

    function init () {
        initMap();
        bindNavigationEvents();
        bindCardEvents();
        bindSearchEvents();
        bindMapEvents();
        setInitialStateFromUrl();
        applyFilters();
    }

    init();
}